import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import * as React from 'react'

import { toast } from '@/hooks/use-toast'

import MarkdownEditor from '@/components/markdown/markdown-editor'
import MarkdownPreview from '@/components/markdown/markdown-preview'
import PaginationComponent from '@/components/PaginationComponent'
import { Button } from '@/components/ui/button'

import { useAuth } from '@/contexts/auth-context'
import blogService from '@/services/blog.service'
import commentService from '@/services/comment.service'

import CommentBlogCard from './comment/comment-blog'

import { CommentReaction, CommentType, CreateCommentReactionDto, EmojiData, ReactionType } from '@/types/comment.type'
import { UserProfile } from '@/types/user.type'

interface BlogCommentsContainerProps {
  blogId: number
  blogSlug: string
}

const TABS = [
  { key: 'write', label: 'Write' },
  { key: 'preview', label: 'Preview' },
]

export default function BlogCommentsContainer({ blogId, blogSlug }: BlogCommentsContainerProps) {
  const queryClient = useQueryClient()
  const router = useRouter()
  const searchParams = useSearchParams()
  const page = Number(searchParams.get('page')) || 1
  const pageSize = 10
  const { user, isAuthenticated } = useAuth()

  const [content, setContent] = React.useState('')
  const [activeTab, setActiveTab] = React.useState<'write' | 'preview'>('write')
  const [submitting, setSubmitting] = React.useState(false)

  // Reply state management
  const [replyStates, setReplyStates] = React.useState<Map<number, boolean>>(new Map())
  const [collapsedReplies, setCollapsedReplies] = React.useState<Map<number, boolean>>(new Map())
  const [replyingTo, setReplyingTo] = React.useState<number | null>(null)
  const [replyContent, setReplyContent] = React.useState('')
  const [replySubmitting, setReplySubmitting] = React.useState(false)

  // Reaction state management
  const [commentReactions, setCommentReactions] = React.useState<Map<number, CommentReaction[]>>(new Map())

  // Edit state management
  const [editingId, setEditingId] = React.useState<number | null>(null)
  const [editingContent, setEditingContent] = React.useState('')

  const {
    data: commentsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['blog-comments', blogId, page, pageSize],
    queryFn: () =>
      blogService.getBlogComments(blogId, {
        params: {
          pageNumber: page,
          pageSize: pageSize,
        },
      }),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  const createCommentMutation = useMutation({
    mutationFn: (commentData: { content: string; blog: { id: number } }) => commentService.createComment(commentData),
    onSuccess: async () => {
      const currentTotalItems = commentsData?.meta?.totalItems || 0
      const newTotalItems = currentTotalItems + 1
      const lastPage = Math.ceil(newTotalItems / pageSize)

      const currentURL = new URL(window.location.href)
      currentURL.searchParams.set('page', lastPage.toString())

      await router.replace(router.asPath.split('?')[0] + currentURL.search, undefined, { shallow: true })

      queryClient.invalidateQueries({ queryKey: ['blog-comments', blogId] })

      setContent('')
      setActiveTab('preview')
    },
    onError: () => {
      if (!isAuthenticated || !user) {
        router.push(`/sign-in?previousPage=${typeof window !== 'undefined' ? window.location.pathname : ''}`)
        return
      }

      toast({ title: 'Error', description: 'Failed to add comment', variant: 'destructive' })
    },
  })

  const createReplyMutation = useMutation({
    mutationFn: (replyData: { content: string; blog: { id: number }; parent: { id: number } }) =>
      commentService.createComment(replyData),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['blog-comments', blogId] })
      setReplyContent('')
      setReplyingTo(null)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setReplyStates(prev => new Map(prev).set(replyingTo!, false))
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to add reply', variant: 'destructive' })
    },
  })

  const updateCommentMutation = useMutation({
    mutationFn: ({ id, content }: { id: number; content: string }) => commentService.updateComment(id, { content }),
    onSuccess: () => {
      toast({ title: 'Updated', description: 'Comment updated successfully' })
      setEditingId(null)
      setEditingContent('')
      queryClient.invalidateQueries({ queryKey: ['blog-comments', blogId] })
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update comment', variant: 'destructive' })
    },
  })

  const handleSubmit = React.useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!content.trim()) return
      setSubmitting(true)

      try {
        await createCommentMutation.mutateAsync({
          content,
          blog: { id: blogId },
        })
      } finally {
        setSubmitting(false)
      }
    },
    [content, blogId, createCommentMutation]
  )

  const handleReplySubmit = React.useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!replyContent.trim() || !replyingTo) return
      setReplySubmitting(true)

      try {
        await createReplyMutation.mutateAsync({
          content: replyContent,
          blog: { id: blogId },
          parent: { id: replyingTo },
        })
        // Expand the parent comment to show the new reply
        setCollapsedReplies(prev => new Map(prev).set(replyingTo, false))
        // Close the reply editor
        setReplyStates(prev => new Map(prev).set(replyingTo, false))
      } finally {
        setReplySubmitting(false)
      }
    },
    [replyContent, replyingTo, blogId, createReplyMutation]
  )

  const onToggleReply = React.useCallback((commentId: number) => {
    setReplyStates(prev => new Map(prev).set(commentId, !prev.get(commentId)))
    setReplyingTo(commentId)
    // When opening reply editor, also expand the parent to show existing children
    setCollapsedReplies(prev => new Map(prev).set(commentId, false))
  }, [])

  const onCloseReply = React.useCallback((commentId: number) => {
    setReplyStates(prev => new Map(prev).set(commentId, false))
    setReplyingTo(null)
    setReplyContent('')
  }, [])

  const onToggleCollapse = React.useCallback((commentId: number) => {
    setCollapsedReplies(prev => {
      return new Map(prev).set(commentId, !prev.get(commentId))
    })
  }, [])

  const handleCommentDeleted = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['blog-comments', blogId] })
  }, [queryClient, blogId])

  const loadCommentReactions = React.useCallback(async (commentId: number) => {
    try {
      const reactions = await commentService.getCommentReactions(commentId)
      setCommentReactions(prev => new Map(prev).set(commentId, reactions))
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load reactions for comment:', commentId, error)
    }
  }, [])

  const createReaction = React.useCallback(
    async (commentId: number, reactionType: ReactionType, emojiData: EmojiData) => {
      if (!isAuthenticated || !user) return

      try {
        const currentReactions = commentReactions.get(commentId) || []
        const existingUserReaction = currentReactions.find(r => r?.user?.id === user?.id)

        const isSameReaction = existingUserReaction && existingUserReaction.reactionType === reactionType

        const reactionDto: CreateCommentReactionDto = {
          reactionType,
          ...(emojiData && { emojiData }),
        }

        const newReaction = await commentService.createCommentReaction(commentId, reactionDto)
        newReaction.user = user as UserProfile

        setCommentReactions(prev => {
          const newMap = new Map(prev)
          const latestReactions = [...(newMap.get(commentId) || [])]

          const filteredReactions = latestReactions.filter(r => r?.user?.id !== user?.id)
          const updatedReactions = isSameReaction ? filteredReactions : [...filteredReactions, newReaction]

          newMap.set(commentId, updatedReactions)
          return newMap
        })

        // Refresh reactions from server
        await loadCommentReactions(commentId)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to create reaction:', error)
        toast({
          title: 'Error',
          description: 'Could not add your reaction',
          variant: 'destructive',
        })
      }
    },
    [isAuthenticated, user, commentReactions, loadCommentReactions]
  )

  // Load reactions for all comments recursively
  const loadReactionsRecursively = React.useCallback(
    (comment: CommentType) => {
      loadCommentReactions(comment.id)

      if (comment.children && comment.children.length > 0) {
        comment.children.forEach(childComment => {
          loadReactionsRecursively(childComment)
        })
      }
    },
    [loadCommentReactions]
  )

  const comments = React.useMemo(() => commentsData?.data || [], [commentsData])
  const totalItems = React.useMemo(() => commentsData?.meta?.totalItems || 0, [commentsData])
  const totalPages = React.useMemo(() => commentsData?.meta?.totalPages || 0, [commentsData])

  // Load reactions when comments change
  React.useEffect(() => {
    if (comments.length > 0) {
      comments.forEach(comment => {
        loadReactionsRecursively(comment)
      })
    }
  }, [comments, loadReactionsRecursively])

  const onStartEdit = React.useCallback((id: number, initial: string) => {
    setEditingId(id)
    setEditingContent(initial ?? '')
    setCollapsedReplies(prev => new Map(prev).set(id, false))
  }, [])

  const onChangeEditContent = React.useCallback((v: string) => {
    setEditingContent(v ?? '')
  }, [])

  const onCancelEdit = React.useCallback(() => {
    setEditingId(null)
    setEditingContent('')
  }, [])

  const onSaveEdit = React.useCallback(async () => {
    if (!editingId || !editingContent.trim()) return
    await updateCommentMutation.mutateAsync({ id: editingId, content: editingContent })
  }, [editingId, editingContent, updateCommentMutation])

  const renderCommentsRecursively = React.useCallback(
    (comments: CommentType[], depth = 0) => {
      return comments.map((comment, index) => {
        const isLast = index === comments.length - 1
        const isReplyOpen = replyStates.get(comment.id) || false
        const hasChildren = comment.children && comment.children.length > 0

        // Initialize collapsed state for comments with children (default to true = collapsed)
        if (hasChildren && !collapsedReplies.has(comment.id)) {
          setCollapsedReplies(prev => new Map(prev).set(comment.id, true))
        }

        const isCollapsed = collapsedReplies.get(comment.id) ?? true

        return (
          <div key={comment.id} className="space-y-4">
            <CommentBlogCard
              comment={comment}
              isLast={isLast}
              blogSlug={blogSlug}
              depth={depth}
              isCollapsed={isCollapsed}
              hasChildren={hasChildren}
              isFirstChild={index === 0}
              onReply={onToggleReply}
              onCollapse={onToggleCollapse}
              onCommentDeleted={handleCommentDeleted}
              reactions={commentReactions.get(comment.id) || []}
              onReact={createReaction}
              currentUser={user}
              // EDIT props
              isEditOpen={editingId === comment.id}
              editingContent={editingContent}
              onChangeEditContent={onChangeEditContent}
              onSaveEdit={onSaveEdit}
              onCancelEdit={onCancelEdit}
              onStartEdit={onStartEdit}
            />

            {/* Reply editor */}
            {isReplyOpen && (
              <div className="ml-12 bg-gray-50 rounded-lg border border-gray-200 p-4">
                <form onSubmit={handleReplySubmit}>
                  <div className="mb-3">
                    <MarkdownEditor
                      id={`reply-editor-${comment.id}`}
                      value={replyContent}
                      onChangeContent={v => setReplyContent(v ?? '')}
                      className="min-h-[100px]"
                      view={{
                        html: false,
                        md: true,
                        menu: true,
                      }}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => onCloseReply(comment.id)}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      size="sm"
                      disabled={replySubmitting || !replyContent.trim() || createReplyMutation.isPending}
                    >
                      {replySubmitting || createReplyMutation.isPending ? 'Posting...' : 'Reply'}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Render child comments - only if not collapsed */}
            {hasChildren && !isCollapsed && comment.children && (
              <div className="ml-8 space-y-4">{renderCommentsRecursively(comment.children, depth + 1)}</div>
            )}
          </div>
        )
      })
    },
    [
      replyStates,
      collapsedReplies,
      blogSlug,
      onToggleReply,
      onToggleCollapse,
      handleCommentDeleted,
      onCloseReply,
      handleReplySubmit,
      replyContent,
      replySubmitting,
      createReplyMutation.isPending,
      commentReactions,
      createReaction,
      user,
      editingId,
      editingContent,
      onChangeEditContent,
      onSaveEdit,
      onCancelEdit,
      onStartEdit,
    ]
  )

  return (
    <div className="w-full mt-10">
      <div>
        <h3 className="text-base font-semibold mb-4">Comments</h3>
        {isLoading ? (
          <div className="text-muted-foreground">Loading comments...</div>
        ) : error ? (
          <div className="text-red-500">Failed to load comments. Please try again.</div>
        ) : comments.length === 0 ? (
          <div className="text-muted-foreground">No comments yet.</div>
        ) : (
          <>
            <ul className="space-y-4 sm:space-y-6">{renderCommentsRecursively(comments)}</ul>

            {totalPages > 1 && (
              <div className="mt-8 flex justify-center">
                <PaginationComponent itemCount={totalItems} pageSize={pageSize} currentPage={page} />
              </div>
            )}
          </>
        )}
      </div>

      <form onSubmit={handleSubmit} className="bg-muted rounded-lg border p-4 mb-8 mt-8">
        <div className="border-b border-border mb-2 flex items-center">
          {TABS.map(tab => (
            <button
              type="button"
              key={tab.key}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors focus:outline-none ${
                activeTab === tab.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'
              }`}
              onClick={() => setActiveTab(tab.key as 'write' | 'preview')}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div>
          {activeTab === 'write' ? (
            <MarkdownEditor
              id="blog-comment-editor"
              value={content}
              onChangeContent={v => setContent(v ?? '')}
              className="min-h-[120px]"
              view={{
                html: false,
                md: true,
                menu: true,
              }}
            />
          ) : (
            <MarkdownPreview className="min-h-[120px] p-3 bg-background rounded border prose prose-sm max-w-none">
              {content || 'Nothing to preview.'}
            </MarkdownPreview>
          )}
        </div>
        <div className="flex justify-end mt-2">
          <Button
            type="submit"
            disabled={submitting || !content.trim() || createCommentMutation.isPending || !isAuthenticated}
          >
            {submitting || createCommentMutation.isPending ? 'Posting...' : 'Comment'}
          </Button>
        </div>
      </form>
    </div>
  )
}
